import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import depositsService from '../../services/deposits.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { pendingAge, sortOldestFirst } from '../../lib/approvalAging';
import { fullName } from '../../lib/fullName';
import { depositReviewReasonLabel } from '../../lib/depositReview';
import { colors, gradients } from '../../styles/theme';
import type { DepositRequest, DepositStatus } from '../../types/finance';

const STATUS_TONE: Record<DepositStatus, BadgeTone> = {
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
  reversed: 'neutral',
};

/** Action-column content for a non-pending request: the rejection reason if
 *  one exists, else an em-dash. Extracted so the action cell stays a single
 *  ternary (pending → buttons, else → this) rather than a nested one. */
function SettledReasonCell({ reason }: Readonly<{ reason: string | null }>): JSX.Element {
  return reason ? (
    <span className="text-xs" style={{ color: colors.muted }}>{reason}</span>
  ) : (
    <span className="text-xs" style={{ color: colors.mutedSoft }}>—</span>
  );
}

/** Source classification badge — communicates whether funds are
 *  already in the QMI concentration account ('wire_received', via
 *  the manual-attribution path) or the manager is pre-approving an
 *  agent-proposed deposit whose wire has not arrived yet
 *  ('client_proposed'). No urgency styling — purely informational. */
function SourceBadge({ source }: Readonly<{ source: 'wire_received' | 'client_proposed' }>): JSX.Element {
  if (source === 'wire_received') {
    return (
      <span
        title="Funds already in QMI concentration account · attributed by ops"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium"
        style={{ background: colors.successBg, color: colors.success,
                 border: `1px solid ${colors.successBorder}` }}
      >
        <span aria-hidden="true">🏦</span> Wire received
      </span>
    );
  }
  return (
    <span
      title="Client commitment registered · waiting for transfer to arrive"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium"
      style={{ background: colors.canvas, color: colors.inkSoft,
               border: `1px solid ${colors.border}` }}
    >
      <span aria-hidden="true">⏱</span> Awaiting wire
    </span>
  );
}

interface DepositActionProps {
  deposit: DepositRequest;
  approveId: string | null;
  approveCode: string;
  actionId: string | null;
  onStartApprove: (id: string) => void;
  onApproveCodeChange: (code: string) => void;
  onConfirmApprove: (d: DepositRequest) => void;
  onCancelApprove: () => void;
  onReject: (d: DepositRequest) => void;
}

/** Action cell for one deposit row: Approve/Reject, or — once Approve is
 *  clicked — an inline step-up code field (money-moving action requires a
 *  fresh TOTP code from the approver). Extracted to keep the table map
 *  render flat (Sonar S3776). */
function renderDepositAction(p: Readonly<DepositActionProps>): JSX.Element {
  const { deposit: d } = p;
  if (d.status !== 'pending') {
    return <SettledReasonCell reason={d.rejection_reason} />;
  }
  if (p.approveId === d.id) {
    return (
      <span className="inline-flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Authentication code"
          value={p.approveCode}
          onChange={(e) => p.onApproveCodeChange(e.target.value.replace(/\D/g, '').slice(0, 8))}
          placeholder="6-digit code"
          className="px-2 py-1.5 rounded-md text-xs font-mono tracking-widest w-28"
          style={{ border: `1px solid ${colors.border}` }}
        />
        <button
          type="button"
          onClick={() => p.onConfirmApprove(d)}
          disabled={p.actionId !== null || p.approveCode.trim().length < 6}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          {p.actionId === d.id ? '…' : 'Confirm'}
        </button>
        <button
          type="button"
          onClick={p.onCancelApprove}
          disabled={p.actionId !== null}
          className="px-3 py-1.5 rounded-md text-xs font-semibold"
          style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
      </span>
    );
  }
  // Approving posts a permanent ledger credit, so it's blocked until the wire
  // has actually arrived (source === 'wire_received'). The backend enforces
  // this too (409 wire_not_received); disabling here avoids a dead-end click.
  const awaitingWire = d.source !== 'wire_received';
  return (
    <span className="inline-flex gap-2">
      <button
        type="button"
        onClick={() => p.onStartApprove(d.id)}
        disabled={p.actionId !== null || awaitingWire}
        title={
          awaitingWire
            ? 'No incoming wire has been reconciled to this deposit yet — it cannot be approved until the transfer arrives.'
            : undefined
        }
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{
          background: awaitingWire ? colors.canvas : gradients.gold,
          color: awaitingWire ? colors.mutedSoft : colors.navy,
          cursor: awaitingWire ? 'not-allowed' : 'pointer',
        }}
      >
        Approve
      </button>
      <button
        type="button"
        onClick={() => p.onReject(d)}
        disabled={p.actionId !== null}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
      >
        Reject
      </button>
    </span>
  );
}

/** 0071: an auto-accepted deposit says so; a pending one says why it waits. */
function ReviewNote({ deposit }: Readonly<{ deposit: DepositRequest }>): JSX.Element | null {
  if (deposit.auto_accepted) {
    return (
      <div className="mt-1 text-[11px] font-medium" style={{ color: colors.success }}>
        Auto-accepted by rules
      </div>
    );
  }
  const reasons = deposit.status === 'pending' ? deposit.auto_review_reasons ?? [] : [];
  if (reasons.length === 0) return null;
  return (
    <div className="mt-1 text-[11px]" style={{ color: colors.inkSoft }}>
      {reasons.map(depositReviewReasonLabel).join(' · ')}
    </div>
  );
}

type StatusFilter = 'pending' | 'all';

export default function ManagerDeposits(): JSX.Element {
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [deposits, setDeposits] = useState<DepositRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<DepositRequest | null>(null);
  // Row awaiting a step-up code before its approval commits, + the code.
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approveCode, setApproveCode] = useState('');
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await depositsService.list({
        status: filter === 'pending' ? ['pending'] : undefined,
        size: 100,
      });
      setDeposits(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load deposits.'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const startApprove = (id: string): void => {
    setError(null);
    setApproveCode('');
    setApproveId(id);
  };

  const cancelApprove = (): void => {
    setApproveId(null);
    setApproveCode('');
  };

  const confirmApprove = async (d: DepositRequest): Promise<void> => {
    const depositorName = fullName({ first_name: d.user_first_name, last_name: d.user_last_name });
    setActionId(d.id);
    setError(null);
    try {
      const res = await depositsService.approve(d.id, d.version, approveCode.trim());
      setApproveId(null);
      setApproveCode('');
      setFlash(
        `Approved. New balance for ${depositorName}: ${formatMoney(res.new_balance, d.currency_code)}.`,
      );
      globalThis.setTimeout(() => setFlash(null), 6000);
      await reload();
    } catch (e: unknown) {
      setError(approveMfaErrorMessage(e));
    } finally {
      setActionId(null);
    }
  };

  let body: JSX.Element;
  if (loading) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (deposits.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        {filter === 'pending'
          ? 'No deposits awaiting your approval.'
          : 'No deposits to display.'}
      </div>
    );
  } else {
    body = (
      <div
        className="overflow-hidden rounded-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Member</th>
              <th className="text-left font-medium px-4 py-3">Product</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-left font-medium px-4 py-3">Reference</th>
              <th className="text-left font-medium px-4 py-3">Funding</th>
              <th className="text-left font-medium px-4 py-3">Proposed by</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {(filter === 'pending' ? sortOldestFirst(deposits) : deposits).map((d, idx) => {
              const age = d.status === 'pending' ? pendingAge(d.created_at) : null;
              return (
              <tr
                key={d.id}
                style={{
                  borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}`,
                  background: age?.isStale ? colors.warningBg : undefined,
                }}
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/manager/clients/${d.user_id}`}
                    state={{ clientName: fullName({ first_name: d.user_first_name, last_name: d.user_last_name }), clientEmail: d.user_email }}
                    className="font-medium hover:underline"
                    style={{ color: colors.ink }}
                  >
                    {fullName({ first_name: d.user_first_name, last_name: d.user_last_name })}
                  </Link>
                  <div className="text-xs" style={{ color: colors.mutedSoft }}>{d.user_email}</div>
                </td>
                <td className="px-4 py-3" style={{ color: colors.inkSoft }}>{d.product_name}</td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                  {formatMoney(d.amount, d.currency_code)}
                </td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                  {d.reference_number ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs">
                  <SourceBadge source={d.source} />
                  <ReviewNote deposit={d} />
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                  <div>{d.proposed_by_email ?? '—'}</div>
                  <div>{formatDateTime(d.created_at)}</div>
                  {age && (
                    <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: age.isStale ? colors.warning : colors.mutedSoft }}>
                      pending {age.label}{age.isStale ? ' · overdue' : ''}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge tone={STATUS_TONE[d.status]}>{d.status}</StatusBadge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {renderDepositAction({
                    deposit: d,
                    approveId,
                    approveCode,
                    actionId,
                    onStartApprove: startApprove,
                    onApproveCodeChange: setApproveCode,
                    onConfirmApprove: confirmApprove,
                    onCancelApprove: cancelApprove,
                    onReject: setRejectTarget,
                  })}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Deposit approvals"
        subtitle="Review deposits agents have proposed for clients. Approving posts a permanent credit to the client's balance — the ledger is append-only, so every entry stays on the record, never edited or deleted."
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="all">All</option>
            <option value="pending">Pending only</option>
          </select>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {body}

      {rejectTarget && (
        <RejectModal
          target={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onSubmitted={(msg) => {
            setRejectTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 5000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface RejectProps {
  target: DepositRequest;
  onClose: () => void;
  onSubmitted: (msg: string) => void;
}

function RejectModal({ target, onClose, onSubmitted }: Readonly<RejectProps>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const trimmed = reason.trim();
  const valid = trimmed.length >= 5 && trimmed.length <= 1000;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      await depositsService.reject(target.id, target.version, trimmed);
      onSubmitted(`Rejected ${formatMoney(target.amount, target.currency_code)} for ${fullName({ first_name: target.user_first_name, last_name: target.user_last_name })}.`);
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not reject.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Reject deposit
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Reason is required and stored in the audit log. The proposing agent
        sees it; the member does not see it verbatim.
      </p>
      <div
        className="mb-4 p-3 rounded-lg text-xs"
        style={{ background: colors.surfaceMuted, color: colors.textPrimary }}
      >
        <div className="font-semibold">{fullName({ first_name: target.user_first_name, last_name: target.user_last_name })}</div>
        <div style={{ color: colors.textMuted }}>
          {formatMoney(target.amount, target.currency_code)} · ref{' '}
          {target.reference_number ?? '—'}
        </div>
      </div>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        maxLength={1000}
        placeholder="e.g. Reference does not match the SPEI receipt."
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />
      <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
        {trimmed.length}/1000 · minimum 5 characters
      </div>

      {err && (
        <div
          className="mt-3 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !valid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !valid ? colors.disabledBg : colors.danger,
            color: busy || !valid ? colors.disabledText : '#FFFFFF',
          }}
        >
          {busy ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Modal>
  );
}
